import React, { useEffect, useState } from 'react';
import { Button, Form, message } from 'antd';
import { AkamLogoCheckIcon, AkamLogoTextIcon, PhoneEditIcon } from 'icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch } from 'store';

import { closeAuthModal, onOpenVerifyBot, useSendSmsV2, useVerifySms } from 'modules/auth';
import { getPlatform } from 'modules/global';

import { OtpStep } from './otp-step';
import { PhoneInput } from './phone-input';
import { SmsMethodSelect } from './sms-method-select';

const { useForm } = Form;

interface IProps {
  setRegisterStep: () => void;
}

export const Login: React.FC<IProps> = ({ setRegisterStep }) => {
  const [form] = useForm();
  const t = useTranslations();
  const { locale = 'uz' } = useRouter();
  const { mutate, isLoading } = useSendSmsV2();
  const { mutate: otpMutate, isLoading: otpLoading } = useVerifySms();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [selectedMethod, setSelectedMethod] = useState<'sms' | 'telegram'>('sms');
  const dispatch = useAppDispatch();

  const onSendOtp = async (values: { phone: string }) => {
    const phone = values?.phone?.replace(/\s|-/g, '');
    const phoneWithoutPrefix = phone?.replace(/^\+998/, '');
    const isPhoneValid = phone?.length === 13;

    if (!isPhoneValid) {
      return form.setFields([
        {
          name: 'phone',
          errors: [t('auth.telefon_raqam_xato')]
        }
      ]);
    }

    if (selectedMethod === 'telegram') {
      setStep('otp');
      return onOpenVerifyBot(phone);
    }

    return mutate(phoneWithoutPrefix, {
      onSuccess: () => {
        setStep('otp');
      }
    });
  };

  const onVerifyOtp = async (values: { phone: string; code: string }) => {
    const { code } = values;
    const phone = values?.phone?.replace(/\s|-/g, '');

    otpMutate(
      { phone, code, platform: getPlatform() },
      {
        onSuccess: data => {
          setStep('phone');
          form.resetFields();
          if (data?.data?.profile_type === null) {
            return setRegisterStep();
          }
          dispatch(closeAuthModal());
          return message.success(t('royxatdan_muvaffaqiyatli_otdingiz'));
        }
      }
    );
  };

  const steps = {
    phone: <SmsMethodSelect selected={selectedMethod} onSelect={setSelectedMethod} />,
    otp: (
      <OtpStep
        selectedMethod={selectedMethod}
        resendLoading={isLoading}
        phone={form.getFieldValue('phone')}
        onSendOtp={onSendOtp}
        onChangePhone={() => setStep('phone')}
      />
    )
  };

  useEffect(() => {
    const readOtpFromSms = async () => {
      if ('OTPCredential' in window || (navigator as any).credentials) {
        try {
          const content = await (navigator as any).credentials.get({
            otp: { transport: ['sms'] },
            signal: new AbortController().signal
          });

          if (content && 'code' in content) {
            form.setFieldsValue({ code: content.code });
            form.submit();
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.log('Web OTP API error or user dismissed:', err);
          }
        }
      }
    };

    readOtpFromSms();
  }, [form]);

  return (
    <>
      <div className="flex items-center justify-center gap-1.5">
        <AkamLogoCheckIcon aria-label="Akam Logo" />
        <AkamLogoTextIcon aria-label="Akam logo text" />
      </div>
      <div className="mt-18.5">
        <h2 className="mb-4 text-center text-xl font-semibold text-gray-800">{t('akkauntingizga_kiring')}</h2>
        <Form
          requiredMark={false}
          form={form}
          layout="vertical"
          onFinish={step === 'phone' ? onSendOtp : onVerifyOtp}
          autoComplete="off"
        >
          <PhoneInput disabled={step === 'otp'} />
          {steps[step]}
          <p className="mt-5 text-center text-sm text-gray-500">
            {t.rich('auth.tugmani_bosish_bilan_siz_foydalanish_shartlariga_rozilik_bildirgan_bolasiz', {
              link: chunks => (
                <Link
                  target="_blank"
                  href={
                    locale === 'uz'
                      ? 'https://docs.google.com/document/d/1qgQQ1bahJXblLZmdbNgOiTvhMDbOA1dWGIbgtcpny30/edit?tab=t.0'
                      : 'https://docs.google.com/document/d/1qgQQ1bahJXblLZmdbNgOiTvhMDbOA1dWGIbgtcpny30/edit?tab=t.fqbxx52cb2am'
                  }
                  className="!text-green-500"
                >
                  {chunks}
                </Link>
              )
            })}
          </p>
          <div className="!mt-6">
            {step === 'otp' && (
              <Button
                onClick={() => setStep('phone')}
                className="!py-5.5 !w-full !border-none !text-blue-500 !shadow-none hover:!underline"
              >
                <PhoneEditIcon />
                {t('auth.nomerni_tahrirlash')}
              </Button>
            )}
            <Button
              htmlType="submit"
              type="primary"
              loading={isLoading || otpLoading}
              className="!h-11 w-full rounded-lg bg-green-500 font-semibold text-white"
            >
              {t('auth.kirish')}
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
};
